import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Card from 'components/card';
import toast from 'react-hot-toast';
import {
  MdArrowBack,
  MdCalendarToday,
  MdAccessTime,
  MdLocationOn,
  MdCheckCircle,
  MdCancel,
  MdGroups,
  MdEdit,
  MdDeleteOutline,
  MdSearch,
  MdPersonAddAlt1,
} from 'react-icons/md';
import { AuthContext } from 'contexts/AuthContext';
import pdSessionService from 'ApiService/PdSession';
import {
  getScheduledDays,
  formatDaySchedules,
} from 'pages/helpers/sessionDays';
import { formatEnrollment } from 'pages/helpers/studentEnrollment';
import AssignSessionModal from 'pages/Common/AssignSessionModal';

// Cycle a student's attendance status: unmarked -> present -> absent -> unmarked.
// Unmarked ('-') means "no lecture / holiday" for that student and is excluded
// from the attendance %, matching the teacher attendance sheet.
const STATUS_CYCLE = { '-': 'P', P: 'A', A: '-' };

const STATUS_STYLES = {
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  completed:
    'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const resolveLayout = (user) => {
  const hrRole = user?.hrRole;
  return `/${
    user?.role === 'placement'
      ? ['headhr', 'jrhr'].includes(hrRole)
        ? hrRole
        : 'placement'
      : user?.role
  }`;
};

const initials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');

const Avatar = ({ name, tone }) => (
  <span
    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${tone}`}
  >
    {initials(name) || '?'}
  </span>
);

const toIsoDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toISOString().slice(0, 10);
};

const getSessionDays = (session) =>
  getScheduledDays(
    session?.startDate,
    session?.endDate,
    session?.daySchedules
  );

const PdSessionDetail = () => {
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const layout = resolveLayout(state?.user);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState('');
  const [presentIds, setPresentIds] = useState([]);
  const [absentIds, setAbsentIds] = useState([]);
  const [editingAttendance, setEditingAttendance] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [rosterSearchTerm, setRosterSearchTerm] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const isAdmin = ['admin', 'superadmin'].includes(state?.user?.role);

  useEffect(() => {
    if (id) fetchSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const sessionDays = useMemo(() => getSessionDays(session), [session]);

  useEffect(() => {
    if (sessionDays.length === 0) return;
    const todayIso = toIsoDate(new Date());
    setSelectedDay((prev) =>
      prev && sessionDays.includes(prev)
        ? prev
        : sessionDays.includes(todayIso)
        ? todayIso
        : sessionDays[0]
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionDays.join(',')]);

  const dayRecord = useMemo(() => {
    if (!session || !selectedDay) return null;
    return (session.attendanceByDay || []).find(
      (day) => toIsoDate(day.date) === selectedDay
    );
  }, [session, selectedDay]);

  useEffect(() => {
    if (!selectedDay) return;
    const present = (dayRecord?.presentStudents || []).map((s) => s._id);
    let absent = (dayRecord?.absentStudents || []).map((s) => s._id);
    // Legacy records (marked before P/A/- existed) only stored presentStudents,
    // where absent was implied as roster - present. Preserve that on load.
    if (dayRecord && !dayRecord.tracksAbsent && present.length > 0) {
      const presentSet = new Set(present);
      absent = (session?.students || [])
        .map((s) => s._id)
        .filter((sid) => !presentSet.has(sid));
    }
    setPresentIds(present);
    setAbsentIds(absent);
    // Viewers who can't mark attendance only ever get the read-only view.
    setEditingAttendance(!dayRecord && Boolean(session?.canMarkAttendance));
    setRosterSearchTerm('');
  }, [selectedDay, dayRecord, session]);

  const fetchSession = async () => {
    try {
      setLoading(true);
      const response = await pdSessionService.getSessionById(id);
      if (response.data.success) {
        setSession(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching PD session:', error);
      toast.error('Failed to fetch PD session');
    } finally {
      setLoading(false);
    }
  };

  const statusOf = (studentId) => {
    if (presentIds.includes(studentId)) return 'P';
    if (absentIds.includes(studentId)) return 'A';
    return '-';
  };

  const setStudentStatus = (studentId, status) => {
    setPresentIds((prev) =>
      status === 'P'
        ? prev.includes(studentId)
          ? prev
          : [...prev, studentId]
        : prev.filter((sid) => sid !== studentId)
    );
    setAbsentIds((prev) =>
      status === 'A'
        ? prev.includes(studentId)
          ? prev
          : [...prev, studentId]
        : prev.filter((sid) => sid !== studentId)
    );
  };

  const cycleStudent = (studentId) => {
    setStudentStatus(studentId, STATUS_CYCLE[statusOf(studentId)]);
  };

  const markAll = (status) => {
    const rosterIds = (session?.students || []).map((s) => s._id);
    setPresentIds(status === 'P' ? rosterIds : []);
    setAbsentIds(status === 'A' ? rosterIds : []);
  };

  const handleSaveAttendance = async () => {
    try {
      setSaving(true);
      const response = await pdSessionService.markAttendance(
        id,
        selectedDay,
        presentIds,
        absentIds
      );
      if (response.data.success) {
        toast.success('Attendance saved successfully');
        setSession(response.data.data);
        setEditingAttendance(false);
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      toast.error(
        error?.response?.data?.message || 'Failed to save attendance'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this PD session? This cannot be undone.'
      )
    ) {
      return;
    }
    try {
      setDeleting(true);
      const response = await pdSessionService.deleteSession(id, true);
      if (response.data.success) {
        toast.success(response.data.message || 'PD session removed');
        navigate(`${layout}/pd-session`);
      }
    } catch (error) {
      console.error('Error deleting PD session:', error);
      toast.error(
        error?.response?.data?.message || 'Failed to delete PD session'
      );
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '-';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;
    if (start.toDateString() === end.toDateString()) {
      return formatDate(startDate);
    }
    return `${formatDate(startDate)} – ${formatDate(endDate)}`;
  };

  const formatDayLabel = (iso) =>
    new Date(iso).toLocaleDateString('en-IN', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    });

  if (loading) {
    return (
      <Card extra="w-full pb-10 p-4">
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-500" />
        </div>
      </Card>
    );
  }

  if (!session) {
    return (
      <Card extra="w-full pb-10 p-4">
        <p className="py-10 text-center text-sm text-gray-500">
          PD session not found.
        </p>
      </Card>
    );
  }

  const totalStudents = session.students?.length || 0;
  const roster = session.students || [];
  const filteredRoster = roster.filter((s) =>
    s.name?.toLowerCase().includes(rosterSearchTerm.trim().toLowerCase())
  );

  // View-mode buckets come from the saved record; edit-mode uses local state.
  const dayPresentStudents = dayRecord?.presentStudents || [];
  const savedAbsent = dayRecord?.absentStudents || [];
  const savedPresentIdSet = new Set(dayPresentStudents.map((p) => p._id));
  const savedAbsentIdSet = new Set(
    dayRecord && !dayRecord.tracksAbsent
      ? // Legacy: absent was implied as roster - present.
        roster.map((s) => s._id).filter((sid) => !savedPresentIdSet.has(sid))
      : savedAbsent.map((p) => p._id)
  );
  // Take every column from `roster` so each row carries the student's enrollment
  // (attendanceByDay.presentStudents is populated with name/email only).
  const viewPresentStudents = roster.filter((s) => savedPresentIdSet.has(s._id));
  const viewAbsentStudents = roster.filter((s) => savedAbsentIdSet.has(s._id));
  const viewNotMarkedStudents = roster.filter(
    (s) => !savedPresentIdSet.has(s._id) && !savedAbsentIdSet.has(s._id)
  );
  const presentCount = savedPresentIdSet.size;
  const absentCount = viewAbsentStudents.length;
  const countedTotal = presentCount + absentCount;
  const presentPct = countedTotal
    ? Math.round((presentCount / countedTotal) * 100)
    : 0;
  const markedDaysCount = (session.attendanceByDay || []).length;
  const scheduleLabel = formatDaySchedules(session.daySchedules);

  return (
    <Card extra="w-full pb-10 p-4">
      <button
        onClick={() => navigate(`${layout}/pd-session`)}
        className="mb-4 flex items-center gap-1 text-sm font-medium text-brand-500 hover:underline"
      >
        <MdArrowBack className="h-4 w-4" />
        Back to list
      </button>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white">
              {session.title}
            </h2>
            <span
              className={`inline-block rounded-full px-3 py-1 text-xs font-semibold capitalize ${
                STATUS_STYLES[session.status] ||
                'bg-gray-100 text-gray-700 dark:bg-navy-700 dark:text-gray-300'
              }`}
            >
              {session.status}
            </span>
          </div>
          {session.createdByName && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
              Created by{' '}
              {session.createdBy === state?.user?.userId
                ? 'You'
                : session.createdByName}{' '}
              ({session.createdByLabel})
            </p>
          )}
          {(session.assignedTo || []).length > 0 && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
              Assigned to{' '}
              {session.assignedTo
                .map((a) =>
                  a.userId === state?.user?.userId
                    ? 'You'
                    : `${a.name} (${a.label})`
                )
                .join(', ')}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-500 dark:text-gray-300">
            <span className="flex items-center gap-1.5">
              <MdCalendarToday className="h-4 w-4" />
              {formatDateRange(session.startDate, session.endDate)}
              {sessionDays.length > 1 && ` (${sessionDays.length} days)`}
            </span>
            {scheduleLabel ? (
              <span className="flex items-center gap-1.5">
                <MdAccessTime className="h-4 w-4" />
                {scheduleLabel}
              </span>
            ) : (
              (session.startTime || session.endTime) && (
                <span className="flex items-center gap-1.5">
                  <MdAccessTime className="h-4 w-4" />
                  {session.startTime}
                  {session.endTime ? ` - ${session.endTime}` : ''}
                  {sessionDays.length > 1 ? ' daily' : ''}
                </span>
              )
            )}
            <span className="flex items-center gap-1.5">
              <MdLocationOn className="h-4 w-4" />
              {(session.branches || []).map((b) => b.name).join(', ') || '-'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 rounded-xl bg-brand-50 px-4 py-3 text-brand-600 dark:bg-navy-700 dark:text-brand-300">
            <MdGroups className="h-6 w-6" />
            <div>
              <p className="text-lg font-bold leading-none">{totalStudents}</p>
              <p className="text-xs font-medium">Students</p>
            </div>
          </div>
          {isAdmin && (
            <button
              onClick={() => setAssignOpen(true)}
              className="flex items-center gap-1 rounded-xl border border-brand-200 px-4 py-2.5 text-sm font-medium text-brand-500 hover:bg-brand-50 dark:border-white/10 dark:hover:bg-white/5"
            >
              <MdPersonAddAlt1 className="h-4 w-4" />
              Assign
            </button>
          )}
          {session.canManage && (
            <>
          <button
            onClick={() => navigate(`${layout}/pd-session/create?id=${id}`)}
            className="flex items-center gap-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-navy-700 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
          >
            <MdEdit className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex items-center gap-1 rounded-xl border border-red-300 px-4 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-60 dark:border-red-900/40 dark:text-red-400 dark:hover:bg-red-900/20"
          >
            <MdDeleteOutline className="h-4 w-4" />
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
            </>
          )}
        </div>
      </div>

      {session.description && (
        <p className="mb-6 rounded-xl bg-gray-50 p-3 text-sm text-navy-700 dark:bg-navy-800 dark:text-white">
          {session.description}
        </p>
      )}

      {sessionDays.length > 1 && (
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div className="min-w-[220px]">
            <label className="ml-1 text-sm font-bold text-navy-700 dark:text-white">
              Attendance Day
            </label>
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              className="mt-2 h-12 w-full rounded-xl border border-gray-200 bg-white/0 p-3 text-sm outline-none dark:!border-white/10 dark:text-white"
            >
              {sessionDays.map((day) => {
                const marked = (session.attendanceByDay || []).some(
                  (d) => toIsoDate(d.date) === day
                );
                return (
                  <option key={day} value={day}>
                    {formatDayLabel(day)}
                    {day === toIsoDate(new Date()) ? ' (Today)' : ''}
                    {marked ? ' ✓ marked' : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <span className="text-xs text-gray-500">
            {markedDaysCount} / {sessionDays.length} days marked
          </span>
        </div>
      )}

      {!editingAttendance && totalStudents > 0 && (
        <div className="mb-6">
          <div className="mb-1.5 flex items-center justify-between text-sm font-medium">
            <span className="text-green-600">
              {presentCount} / {countedTotal} present ({presentPct}%)
            </span>
            <span className="text-red-500">
              {absentCount} absent
              {viewNotMarkedStudents.length > 0 &&
                ` · ${viewNotMarkedStudents.length} not marked`}
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-red-100 dark:bg-red-900/30">
            <div
              className="h-full rounded-full bg-green-500 transition-all"
              style={{ width: `${presentPct}%` }}
            />
          </div>
        </div>
      )}

      <h3 className="mb-3 text-lg font-bold text-navy-700 dark:text-white">
        Roster ({totalStudents})
        {selectedDay && (
          <span className="ml-2 text-sm font-normal text-gray-500">
            — {formatDate(selectedDay)}
          </span>
        )}
      </h3>

      {editingAttendance ? (
        <>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-200 px-3 py-2 dark:border-white/10">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => markAll('P')}
                className="rounded-lg bg-green-100 px-3 py-1 text-xs font-semibold text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400"
              >
                Mark all present
              </button>
              <button
                type="button"
                onClick={() => markAll('A')}
                className="rounded-lg bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400"
              >
                Mark all absent
              </button>
              <button
                type="button"
                onClick={() => markAll('-')}
                className="rounded-lg bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-300"
              >
                Clear
              </button>
            </div>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {presentIds.length} P · {absentIds.length} A ·{' '}
              {totalStudents - presentIds.length - absentIds.length} —
            </span>
          </div>
          <p className="mb-2 ml-1 text-xs text-gray-500">
            Tap the status button to cycle — <b>P</b> present · <b>A</b> absent ·{' '}
            <b>—</b> no lecture / holiday (not counted in %).
          </p>

          <div className="relative mb-2">
            <input
              type="text"
              value={rosterSearchTerm}
              onChange={(e) => setRosterSearchTerm(e.target.value)}
              placeholder="Search by name..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white md:max-w-xs"
            />
            <MdSearch className="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-gray-500 dark:text-gray-400" />
          </div>

          <div className="max-h-96 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10">
            {filteredRoster.length === 0 && rosterSearchTerm.trim() && (
              <p className="p-3 text-center text-sm text-gray-500">
                No students match &quot;{rosterSearchTerm}&quot;.
              </p>
            )}
            {filteredRoster.map((student) => {
              const status = statusOf(student._id);
              return (
                <div
                  key={student._id}
                  className="flex items-center gap-3 border-b border-gray-100 p-3 text-sm last:border-b-0 dark:border-white/5"
                >
                  <button
                    type="button"
                    onClick={() => cycleStudent(student._id)}
                    className={`h-8 w-8 shrink-0 rounded-lg text-sm font-bold transition ${
                      status === 'P'
                        ? 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400'
                        : status === 'A'
                        ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200 dark:bg-navy-700 dark:text-gray-500'
                    }`}
                  >
                    {status}
                  </button>
                  <Avatar
                    name={student.name}
                    tone={
                      status === 'P'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : status === 'A'
                        ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300'
                        : 'bg-gray-100 text-gray-500 dark:bg-navy-700 dark:text-gray-300'
                    }
                  />
                  <div>
                    <p className="font-medium text-navy-700 dark:text-white">
                      {student.name}
                    </p>
                    <p className="text-xs text-gray-500">{student.email}</p>
                    {formatEnrollment(student) && (
                      <p className="text-xs text-gray-400">
                        {formatEnrollment(student)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleSaveAttendance}
              disabled={saving}
              className="rounded-xl bg-brand-500 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-600 disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save Attendance'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-green-600">
                <MdCheckCircle className="h-4 w-4" />
                Present ({presentCount})
              </h4>
              <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10">
                {presentCount === 0 ? (
                  <p className="p-3 text-sm text-gray-500">No students present.</p>
                ) : (
                  viewPresentStudents.map((student) => (
                    <div
                      key={student._id}
                      className="flex items-center gap-3 border-b border-gray-100 p-3 text-sm last:border-b-0 dark:border-white/5"
                    >
                      <Avatar
                        name={student.name}
                        tone="bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                      />
                      <div>
                        <p className="font-medium text-navy-700 dark:text-white">
                          {student.name}
                        </p>
                        {formatEnrollment(student) && (
                          <p className="text-xs text-gray-400">
                            {formatEnrollment(student)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-red-500">
                <MdCancel className="h-4 w-4" />
                Absent ({viewAbsentStudents.length})
              </h4>
              <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10">
                {viewAbsentStudents.length === 0 ? (
                  <p className="p-3 text-sm text-gray-500">No students absent.</p>
                ) : (
                  viewAbsentStudents.map((student) => (
                    <div
                      key={student._id}
                      className="flex items-center gap-3 border-b border-gray-100 p-3 text-sm last:border-b-0 dark:border-white/5"
                    >
                      <Avatar
                        name={student.name}
                        tone="bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300"
                      />
                      <div>
                        <p className="font-medium text-navy-700 dark:text-white">
                          {student.name}
                        </p>
                        {formatEnrollment(student) && (
                          <p className="text-xs text-gray-400">
                            {formatEnrollment(student)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div>
              <h4 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-gray-500">
                <MdAccessTime className="h-4 w-4" />
                Not marked ({viewNotMarkedStudents.length})
              </h4>
              <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200 dark:border-white/10">
                {viewNotMarkedStudents.length === 0 ? (
                  <p className="p-3 text-sm text-gray-500">
                    Everyone is marked.
                  </p>
                ) : (
                  viewNotMarkedStudents.map((student) => (
                    <div
                      key={student._id}
                      className="flex items-center gap-3 border-b border-gray-100 p-3 text-sm last:border-b-0 dark:border-white/5"
                    >
                      <Avatar
                        name={student.name}
                        tone="bg-gray-100 text-gray-500 dark:bg-navy-700 dark:text-gray-300"
                      />
                      <div>
                        <p className="font-medium text-navy-700 dark:text-white">
                          {student.name}
                        </p>
                        {formatEnrollment(student) && (
                          <p className="text-xs text-gray-400">
                            {formatEnrollment(student)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            "Not marked" = no lecture / holiday for that student — not counted in
            the attendance %.
          </p>
          {session.canMarkAttendance && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setEditingAttendance(true)}
              className="rounded-xl border border-gray-300 px-5 py-2 text-sm font-medium text-navy-700 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
            >
              Edit Attendance
            </button>
          </div>
          )}
        </>
      )}

      <AssignSessionModal
        isOpen={assignOpen}
        session={session}
        service={pdSessionService}
        onClose={() => setAssignOpen(false)}
        onSaved={(updated) =>
          setSession((prev) => ({ ...prev, assignedTo: updated.assignedTo }))
        }
      />
    </Card>
  );
};

export default PdSessionDetail;
